import { useEffect, useState } from "react";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const AdminDonations = () => {
  const [donations, setDonations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const token = localStorage.getItem("nox_admin_token") || "";
      const res = await fetch(`${API}/api/admin/donations`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 401) {
        setError("Not authorized.");
        setLoaded(true);
        return;
      }
      if (!res.ok) throw new Error();
      setDonations(await res.json());
      setLoaded(true);
    } catch {
      setError("Could not load the offerings.");
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (id, approved) => {
    setBusy(id);
    setError("");
    try {
      const token = localStorage.getItem("nox_admin_token") || "";
      const res = await fetch(`${API}/api/admin/donations/${id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ approved }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Action failed");
      await load();
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-10 space-y-3" data-testid="admin-donations">
      <p className="text-xs leading-relaxed text-slate-500">
        Offerings left by visitors. Only paid ones you approve appear on the public wall inside the Support modal.
      </p>
      {error && (
        <p className="text-xs text-red-500" data-testid="donations-error">
          {error}
        </p>
      )}
      {!loaded && (
        <div className="flex items-center justify-center gap-4 py-10">
          <MoonMark className="h-6 w-6 animate-spin text-gold" style={{ animationDuration: "6s" }} />
          <span className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Summoning…</span>
        </div>
      )}
      {loaded && donations.length === 0 && (
        <p className="border border-line bg-surface p-6 text-center font-cormorant text-lg italic text-slate-500">
          No offerings yet — the wall awaits its first word.
        </p>
      )}
      {donations.map((d, i) => (
        <div key={d.id} className="border border-line bg-surface p-5" data-testid={`donation-row-${i}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-display text-lg text-silver">
              {d.name} <span className="font-jmono text-xs tracking-[0.2em] text-gold">· {fmt(d.amount)}</span>
            </p>
            <div className="flex items-center gap-3">
              <span
                className={`border px-3 py-1 font-jmono text-[9px] uppercase tracking-[0.25em] ${
                  d.status === "paid" ? "border-emerald-500/60 text-emerald-400" : "border-line text-slate-500"
                }`}
                data-testid={`donation-status-${i}`}
              >
                {d.status === "paid" ? "Paid" : "Awaiting payment"}
              </span>
              {d.status === "paid" && (
                <button
                  className={`cursor-pointer border px-3 py-1.5 font-jmono text-[9px] uppercase tracking-[0.25em] transition-colors ${
                    d.approved
                      ? "border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10"
                      : "border-gold/60 text-gold hover:bg-gold/10"
                  } disabled:opacity-40`}
                  onClick={() => approve(d.id, !d.approved)}
                  disabled={busy === d.id}
                  data-testid={`donation-approve-${i}`}
                >
                  {busy === d.id ? "…" : d.approved ? "On the wall — unapprove" : "Approve for the wall"}
                </button>
              )}
            </div>
          </div>
          {d.comment && (
            <p className="mt-3 border-l-2 border-gold/40 pl-4 text-sm font-light italic leading-relaxed text-slate-300">
              "{d.comment}"
            </p>
          )}
          <p className="mt-3 font-jmono text-[9px] tracking-[0.25em] text-slate-600">
            {d.created_at ? new Date(d.created_at).toLocaleString() : ""}
          </p>
        </div>
      ))}
    </div>
  );
};

export default AdminDonations;
